import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { CoinsIcon, CornerDownRightIcon, HouseIcon, LayoutDashboardIcon, MousePointerClickIcon, PlusIcon } from 'lucide-react';
import { calcHistory, commissionLines, loanDetails, loanRecord, referenceExamples } from '../data/loanCommission';
import { lockedPeriods, manualLinePayees } from '../data/people';
import { Button } from '../components/sf/Button';
import { Card } from '../components/sf/Card';
import { NewBadge } from '../components/sf/NewBadge';
import { RecordHeader } from '../components/sf/RecordHeader';
import { StatusBadge } from '../components/sf/StatusBadge';
import { Tabs } from '../components/sf/Tabs';
import { ManualLineModal, ManualLineMode } from '../components/commission/ManualLineModal';
import { CalculationHistory } from '../components/loan/CalculationHistory';
import { CommissionLinesTable } from '../components/loan/CommissionLinesTable';
import { ReferenceExamplesCard } from '../components/loan/ReferenceExamplesCard';
import { WhyAmountPanel } from '../components/loan/WhyAmountPanel';
import { CommissionLine, ManualLineInput } from '../types/commission';
import { ScreenId } from '../types/navigation';
import { formatUsd } from '../utils/format';
type LoanTab = 'commission' | 'details';
interface LoanCommissionProps {
  onNavigate: (screen: ScreenId) => void;
}
function buildManualLine(input: ManualLineInput, mode: ManualLineMode): CommissionLine {
  const isAdjustment = mode === 'adjustment';
  const trace = ['Added just now (NEW — manual lines with a reason are not in main today)', `Reason: “${input.reason}”`, ...(input.splitPct !== null ? [`Split: ${input.payee} receives ${input.splitPct}% of this loan`] : []), ...(isAdjustment ? [`${input.sourcePeriod} is locked, so this posts to ${loanRecord.nextOpenPeriod}, the next open period`] : [])];
  return {
    id: `manual-${Date.now()}`,
    loanNumber: loanRecord.loanNumber,
    payee: input.payee,
    payeeRole: isAdjustment ? 'Adjustment' : 'Manual line',
    component: isAdjustment ? 'Adjustment' : 'Manual',
    ruleKey: isAdjustment ? 'ADJ-NEW' : 'MANUAL',
    bps: null,
    payAmount: input.amount,
    adjustedAmount: input.amount,
    splitPct: input.splitPct ?? undefined,
    period: isAdjustment ? input.sourcePeriod ?? '' : loanRecord.period,
    periodStatus: isAdjustment ? 'Locked' : 'Open',
    postedTo: isAdjustment ? loanRecord.nextOpenPeriod : undefined,
    status: isAdjustment ? 'Adjustment' : 'Manual',
    trace,
    formulas: [`Fixed amount ${formatUsd(input.amount)} — same on both bases`],
    snapshot: [{
      label: 'Payee',
      value: input.payee
    }, {
      label: 'Amount',
      value: formatUsd(input.amount)
    }, {
      label: 'Split',
      value: input.splitPct !== null ? `${input.splitPct}%` : '—'
    }]
  };
}
export function LoanCommission({
  onNavigate
}: LoanCommissionProps) {
  const [tab, setTab] = useState<LoanTab>('commission');
  const [lines, setLines] = useState<CommissionLine[]>(commissionLines);
  const [selectedId, setSelectedId] = useState<string | null>(commissionLines[0].id);
  const [modalMode, setModalMode] = useState<ManualLineMode | null>(null);
  const selectedLine = [...lines, ...referenceExamples].find(line => line.id === selectedId) ?? null;
  const goToPlan = () => onNavigate('plans');
  function handleSubmit(input: ManualLineInput) {
    if (!modalMode) return;
    const line = buildManualLine(input, modalMode);
    setLines(prev => [...prev, line]);
    setSelectedId(line.id);
    setModalMode(null);
    toast.success(modalMode === 'adjustment' ? `Adjustment posted to ${loanRecord.nextOpenPeriod}` : 'Manual line added', {
      description: `${input.payee} · ${formatUsd(input.amount)}`
    });
  }
  return <div className="space-y-3" data-id="element-1163" data-mp="161cnj1oi2bno">
      <RecordHeader objectLabel="Loan" title={loanRecord.loanNumber} icon={HouseIcon} iconBg="#3BA755" actions={<Button icon={<LayoutDashboardIcon data-id="element-1166" data-mp="2ujnv97qt5ta5" />} onClick={() => onNavigate('statement')} data-id="element-1165" data-mp="2uj5sds9n4gt9">
            Open LO Dashboard
          </Button>} fields={[{
      label: 'Borrower',
      value: loanRecord.borrower
    }, {
      label: 'Loan Amount',
      value: <span className="tabular-nums" data-id="element-1167" data-mp="3993gk87kl6ig">{formatUsd(loanRecord.loanAmount)}</span>
    }, {
      label: 'Adjusted Loan Amount',
      value: <span className="tabular-nums" data-id="element-1168" data-mp="2bm4j3v91zuji">{formatUsd(loanRecord.adjustedAmount)}</span>
    }, {
      label: 'Funded Date',
      value: loanRecord.fundedDate
    }, {
      label: 'Loan Officer',
      value: <span className="text-sf-blue" data-id="element-1169" data-mp="2lizggatlbow7">{loanRecord.loanOfficer}</span>
    }, {
      label: 'Branch',
      value: loanRecord.branch
    }, {
      label: 'Comp Type',
      value: loanRecord.compType
    }, {
      label: 'Product',
      value: loanRecord.product
    }, {
      label: 'Lead Source',
      value: loanRecord.leadSource
    }, {
      label: 'Commission Period',
      value: <span className="inline-flex items-center gap-1.5" data-id="element-1170" data-mp="1qa5j1ka2q2r0">
                {loanRecord.period} <StatusBadge status={loanRecord.periodStatus} data-id="element-1171" data-mp="1lbc4rpmyq7ps" />
              </span>
    }]} data-id="element-1164" data-mp="15y6wvi0831pu" />

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_400px]" data-id="element-1172" data-mp="1gju6j9cbql8i">
        <section className="rounded border border-sf-border bg-white shadow-card" data-id="element-1173" data-mp="1bm0y09njtf52">
          <Tabs label="Loan sections" tabs={[{
          id: 'commission',
          label: 'Commission'
        }, {
          id: 'details',
          label: 'Details'
        }]} active={tab} onChange={setTab} data-id="element-1174" data-mp="16m6beiat3bpt" />
          <div className="space-y-3 p-3" data-id="element-1175" data-mp="xlzz2zqughs8">
            {tab === 'commission' ? <>
                <Card title={`Commission Lines (${lines.length})`} icon={CoinsIcon} iconBg="#0176D3" meta="NEW: one line per payee × component. Today there is one fee row per payee per loan." bodyClassName="" actions={<>
                      <NewBadge data-id="element-1177" data-mp="q2gyxgpgy7wf" />
                      <Button size="sm" icon={<CornerDownRightIcon data-id="element-1179" data-mp="q41v1arflywe" />} onClick={() => setModalMode('adjustment')} data-id="element-1178" data-mp="q3s5tpe7nvrr">
                        Add Adjustment
                      </Button>
                      <Button size="sm" icon={<PlusIcon data-id="element-1181" data-mp="gcp54op71it0" />} onClick={() => setModalMode('manual')} data-id="element-1180" data-mp="gd78s6e6qzay">
                        Add Manual Line
                      </Button>
                    </>} data-id="element-1176" data-mp="sohnt2pctqh2">
                  <CommissionLinesTable lines={lines} selectedId={selectedId} onSelect={setSelectedId} onRuleKeyClick={goToPlan} showTotal data-id="element-1182" data-mp="18z0byh3e8fy9" />
                </Card>
                <CalculationHistory entries={calcHistory} data-id="element-1183" data-mp="z1vli52ilybn" />
                <ReferenceExamplesCard lines={referenceExamples} selectedId={selectedId} onSelect={setSelectedId} onRuleKeyClick={goToPlan} data-id="element-1184" data-mp="3eu1g91juad4h" />
              </> : <dl className="grid grid-cols-2 gap-x-8 gap-y-4 px-2 py-2 md:grid-cols-3" data-id="element-1185" data-mp="3jr1jz5t7v3sf">
                {loanDetails.map(field => <div key={field.label} className="border-b border-sf-border pb-2" data-id="element-1186" data-mp="1wwy182fuhted">
                    <dt className="text-xs text-sf-weak" data-id="element-1187" data-mp="2avxop6rmg28y">{field.label}</dt>
                    <dd className="mt-0.5 text-[13px]" data-id="element-1188" data-mp="2au3ascerpaed">{field.value}</dd>
                  </div>)}
              </dl>}
          </div>
        </section>

        <div className="lg:sticky lg:top-[100px] lg:max-h-[calc(100vh-112px)] lg:overflow-y-auto" data-id="element-1189" data-mp="1j5jd5h72z20g">
          <AnimatePresence mode="wait" initial={false} data-id="element-1190" data-mp="1e7hugxdkh4pi">
            {selectedLine ? <motion.div key={selectedLine.id} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 12
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-1191" data-mp="1ry4tkqn8owm4">
                <WhyAmountPanel line={selectedLine} onClose={() => setSelectedId(null)} data-id="element-1192" data-mp="ventx8goxf4t" />
              </motion.div> : <motion.div key="empty" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} className="flex flex-col items-center rounded border border-dashed border-sf-border-strong bg-white px-6 py-10 text-center" data-id="element-1193" data-mp="1kcaw63d1vmib">
                <MousePointerClickIcon className="h-6 w-6 text-sf-weak" aria-hidden="true" data-id="element-1194" data-mp="238kitf20pldx" />
                <p className="mt-2 text-[13px] font-semibold" data-id="element-1195" data-mp="286m2o279poqg">Why this amount?</p>
                <p className="mt-1 text-xs text-sf-weak" data-id="element-1196" data-mp="2czyfzg6hy93w">Select any commission line to trace it back to its rule, Adjusted Loan Amount and inputs.</p>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <ManualLineModal open={modalMode !== null} mode={modalMode ?? 'manual'} context={`${loanRecord.loanNumber} · ${loanRecord.borrower}`} payees={manualLinePayees} defaultPayee={loanRecord.loanOfficer} postsTo={loanRecord.nextOpenPeriod} lockedPeriods={lockedPeriods} onClose={() => setModalMode(null)} onSubmit={handleSubmit} data-id="element-1197" data-mp="2lxh792aqhjo8" />
    </div>;
}